/**
 * Model Registry — Pricing & Capabilities from the configured provider's /models endpoint.
 *
 * Caches model metadata (pricing, capabilities) per session and per base URL.
 * Used to determine cost estimates and feature support (vision, image-gen, etc.)
 */

// ────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────

export interface ModelPricing {
    input: number   // cost per token for input
    output: number  // cost per token for output
}

/** What multimodal capabilities a model supports */
export interface ModelCapabilities {
    vision: boolean      // can receive image inputs
    imageGen: boolean    // can generate images
    audio: boolean       // can receive audio inputs
    pdf: boolean         // can receive PDF inputs
    video: boolean       // can receive video inputs
}

export interface ModelInfo {
    id: string
    pricing: ModelPricing
    capabilities: ModelCapabilities
}

// ────────────────────────────────────────────────────────────────
// Cache & Fetching
// ────────────────────────────────────────────────────────────────

const DEFAULT_MODELS_BASE_URL = 'https://home.hoglundit.se/v1'
const modelsCache = new Map<string, Record<string, ModelInfo>>()
const modelsFetchPromise = new Map<string, Promise<Record<string, ModelInfo>>>()

/** Default pricing when model-specific data is unavailable */
export const DEFAULT_PRICING: ModelPricing = { input: 2e-6, output: 8e-6 }

/** Default capabilities — assume text only */
const DEFAULT_CAPABILITIES: ModelCapabilities = {
    vision: false,
    imageGen: false,
    audio: false,
    pdf: false,
    video: false,
}

/**
 * Fetch and cache model metadata from the current provider's models API.
 * Returns a map of model ID → ModelInfo.
 */
export async function fetchModelsInfo(baseUrl?: string): Promise<Record<string, ModelInfo>> {
    return fetchModelsInfoWithAuth(baseUrl)
}

/** Get pricing for a specific model */
export async function getTokenPrice(model: string, baseUrl?: string, apiKey?: string): Promise<ModelPricing> {
    const info = await fetchModelsInfoWithAuth(baseUrl, apiKey)
    return info[model]?.pricing ?? DEFAULT_PRICING
}

/** Get capabilities for a specific model */
export async function getModelCapabilities(model: string, baseUrl?: string, apiKey?: string): Promise<ModelCapabilities> {
    const info = await fetchModelsInfoWithAuth(baseUrl, apiKey)
    return info[model]?.capabilities ?? DEFAULT_CAPABILITIES
}

/** Reset the cache (mainly for testing) */
export function resetModelsCache(): void {
    modelsCache.clear()
    modelsFetchPromise.clear()
}

function buildModelsUrl(baseUrl?: string): string {
    const normalizedBaseUrl = (baseUrl || DEFAULT_MODELS_BASE_URL).trim().replace(/\/+$/, '')
    return `${normalizedBaseUrl}/models`
}

async function fetchModelsInfoWithAuth(baseUrl?: string, apiKey?: string): Promise<Record<string, ModelInfo>> {
    const modelsUrl = buildModelsUrl(baseUrl)
    const cacheKey = `${modelsUrl}|${apiKey?.trim() ? `auth:${apiKey.trim()}` : 'anon'}`
    const cached = modelsCache.get(cacheKey)
    if (cached) return cached

    const pending = modelsFetchPromise.get(cacheKey)
    if (pending) return pending

    const request = (async () => {
        try {
            const res = await fetch(modelsUrl, {
                headers: buildModelsHeaders(apiKey),
            })
            if (!res.ok) throw new Error(`Models API: ${res.status}`)
            const json = await res.json() as {
                data: Array<{
                    id: string
                    pricing?: { prompt?: string; completion?: string }
                    architecture?: {
                        modality?: string
                        input_modalities?: string[]
                        output_modalities?: string[]
                    }
                }>
            }
            const map: Record<string, ModelInfo> = {}
            for (const m of json.data) {
                const pricing: ModelPricing = m.pricing ? {
                    input: parseFloat(m.pricing.prompt ?? '0'),
                    output: parseFloat(m.pricing.completion ?? '0'),
                } : DEFAULT_PRICING

                const modality = m.architecture?.modality ?? ''
                const inputMods = m.architecture?.input_modalities ?? []
                const outputMods = m.architecture?.output_modalities ?? []

                const capabilities: ModelCapabilities = {
                    vision: modality.includes('image->') || inputMods.includes('image'),
                    imageGen: modality.includes('->') && modality.split('->')[1]?.includes('image')
                        || outputMods.includes('image'),
                    audio: modality.includes('audio') || inputMods.includes('audio'),
                    pdf: inputMods.includes('pdf') || inputMods.includes('file'),
                    video: modality.includes('video') || inputMods.includes('video'),
                }

                map[m.id] = { id: m.id, pricing, capabilities }
            }
            modelsCache.set(cacheKey, map)
            return map
        } catch (err) {
            console.warn(`[Models] Failed to fetch model info from ${modelsUrl}, using defaults:`, err)
            return {}
        } finally {
            modelsFetchPromise.delete(cacheKey)
        }
    })()

    modelsFetchPromise.set(cacheKey, request)
    return request
}

function buildModelsHeaders(apiKey?: string): HeadersInit | undefined {
    const trimmed = apiKey?.trim()
    if (!trimmed) return undefined
    return { Authorization: `Bearer ${trimmed}` }
}
